/**
 * Flow ID Manager
 * 
 * Manages the flow_id that connects events across sessions.
 * Creates on CTA Click or Wizard Start, persists in sessionStorage with TTL.
 */

import { STORAGE_KEYS, FLOW_ID_TTL_MS } from './constants';

/**
 * Generate a UUID v4
 */
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

/**
 * Get current flow_id if valid (not expired)
 * @returns {string|null}
 */
export function getFlowId() {
  try {
    const flowId = sessionStorage.getItem(STORAGE_KEYS.FLOW_ID);
    const timestamp = sessionStorage.getItem(STORAGE_KEYS.FLOW_ID_TIMESTAMP);
    
    if (!flowId || !timestamp) {
      return null;
    }
    
    const age = Date.now() - parseInt(timestamp, 10);
    if (age > FLOW_ID_TTL_MS) {
      // Expired
      clearFlowId();
      return null;
    }
    
    return flowId;
  } catch (e) {
    console.warn('[Analytics] Error reading flow_id:', e);
    return null;
  }
}

/**
 * Create a new flow_id
 * @returns {string} The new flow_id
 */
export function createFlowId() {
  const flowId = generateUUID();
  try {
    sessionStorage.setItem(STORAGE_KEYS.FLOW_ID, flowId);
    sessionStorage.setItem(STORAGE_KEYS.FLOW_ID_TIMESTAMP, Date.now().toString());
  } catch (e) {
    console.warn('[Analytics] Error saving flow_id:', e);
  }
  return flowId;
}

/**
 * Get existing flow_id or create new one
 * @returns {string}
 */
export function getOrCreateFlowId() {
  const existing = getFlowId();
  if (existing) {
    return existing;
  }
  return createFlowId();
}

/**
 * Clear flow_id (call when flow is complete, e.g., after save)
 */
export function clearFlowId() {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.FLOW_ID);
    sessionStorage.removeItem(STORAGE_KEYS.FLOW_ID_TIMESTAMP);
  } catch (e) {
    console.warn('[Analytics] Error clearing flow_id:', e);
  }
}

/**
 * Check if flow_id exists and is valid
 * @returns {boolean}
 */
export function hasValidFlowId() {
  return getFlowId() !== null;
}
